class HeroSlider extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-hero-track]');
    this.slides = Array.from(this.querySelectorAll('[data-hero-slide]'));
    this.total = this.slides.length;
    if (!this.track || this.total === 0) return;

    this.index = 0;
    this.autoplay = this.dataset.autoplay !== 'false';
    this.interval = parseInt(this.dataset.speed || '7', 10) * 1000;
    this.timer = null;
    this.remaining = null;
    this.startedAt = null;
    this.activeDelay = this.interval;
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.style.setProperty('--hero-autoplay-ms', `${this.interval}ms`);
    this.classList.toggle('has-autoplay', this.autoplay);

    this.dots = Array.from(this.querySelectorAll('[data-hero-dot]'));
    this.prevBtn = this.querySelector('[data-hero-prev]');
    this.nextBtn = this.querySelector('[data-hero-next]');

    this.touchStartX = 0;
    this.touchDeltaX = 0;

    this.prevBtn?.addEventListener('click', () => this.go(this.index - 1, true));
    this.nextBtn?.addEventListener('click', () => this.go(this.index + 1, true));
    this.addEventListener('touchstart', (event) => {
      this.touchStartX = event.changedTouches[0].screenX;
      this.touchDeltaX = 0;
    }, { passive: true });
    this.addEventListener('touchmove', (event) => {
      this.touchDeltaX = event.changedTouches[0].screenX - this.touchStartX;
    }, { passive: true });
    this.addEventListener('touchend', () => {
      if (Math.abs(this.touchDeltaX) < 40) return;
      this.go(this.touchDeltaX > 0 ? this.index - 1 : this.index + 1, true);
    });
    this.dots.forEach((dot) => {
      dot.addEventListener('click', () => {
        const i = parseInt(dot.dataset.heroDot, 10);
        if (!Number.isNaN(i)) this.go(i, true);
      });
    });

    this.addEventListener('mouseenter', () => this.pause());
    this.addEventListener('mouseleave', () => this.resume());
    this.addEventListener('focusin', () => this.pause());
    this.addEventListener('focusout', (e) => {
      if (!this.contains(e.relatedTarget)) this.resume();
    });

    this.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        this.go(this.index - 1, true);
      }
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        this.go(this.index + 1, true);
      }
    });

    this.reducedMotion.addEventListener('change', () => {
      if (this.reducedMotion.matches) this.pause();
      else this.resume();
    });

    this.onBlockSelectHandler = (event) => {
      const slide = event.target.closest('[data-hero-slide]');
      if (!slide || !this.contains(slide)) return;
      const i = parseInt(slide.dataset.heroSlide, 10);
      if (!Number.isNaN(i)) this.go(i, true);
    };
    document.addEventListener('shopify:block:select', this.onBlockSelectHandler);

    if (this.total <= 1) this.classList.add('is-single');
    else this.setAttribute('tabindex', '0');

    this.go(0, false);
    this.resume();
  }

  disconnectedCallback() {
    this.pause();
    document.removeEventListener('shopify:block:select', this.onBlockSelectHandler);
  }

  go(nextIndex, userTriggered) {
    if (this.total <= 1) return;
    this.index = (nextIndex + this.total) % this.total;
    this.track.style.transform = `translate3d(-${this.index * 100}%, 0, 0)`;

    this.slides.forEach((slide, i) => {
      const active = i === this.index;
      slide.classList.toggle('is-active', active);
      slide.setAttribute('aria-hidden', active ? 'false' : 'true');
      slide.inert = !active;
    });

    this.dots.forEach((dot, i) => {
      const active = i === this.index;
      dot.classList.toggle('is-active', active);
      dot.setAttribute('aria-selected', active ? 'true' : 'false');
      const progress = dot.querySelector('[data-hero-progress]');
      if (!progress) return;
      progress.style.animation = 'none';
      if (active && this.autoplay && !this.reducedMotion.matches) {
        void progress.offsetWidth;
        progress.style.animation = '';
      }
    });

    if (userTriggered) {
      this.remaining = null;
      this.stopTimer();
      this.classList.add('is-paused');
      this.resume();
    }
  }

  play() {
    if (!this.autoplay || this.total <= 1 || this.reducedMotion.matches) return;
    this.stopTimer();
    this.classList.remove('is-paused');
    const delay = this.remaining != null ? this.remaining : this.interval;
    this.startedAt = Date.now();
    this.activeDelay = delay;
    this.timer = window.setTimeout(() => {
      this.remaining = null;
      this.go(this.index + 1, false);
      this.play();
    }, delay);
  }

  stopTimer() {
    if (!this.timer) return;
    window.clearTimeout(this.timer);
    this.timer = null;
  }

  pause() {
    if (this.timer && this.startedAt != null) {
      this.remaining = Math.max(50, this.activeDelay - (Date.now() - this.startedAt));
    }
    this.stopTimer();
    this.classList.add('is-paused');
  }

  resume() {
    if (this.autoplay && !this.reducedMotion.matches) this.play();
  }
}

customElements.define('hero-slider', HeroSlider);
